
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation';
import { useProject } from '@/Hooks/UseProject';
import { useTeams } from '@/Hooks/UseTeam';

import { getExcelFirstSheetData, getExcelSecondSheetData } from '@/Helpers/NewDashboardHelper';
import { filterByColorCodes, filterByDate, filterByGroups, filterByStatus, filterByTypes, filterByUsers, getCollapsedGroups, getCollapsedTasks, getMacroResult } from '@/Helpers/ProgrammationHelper';

import { setMacroAction } from '@/Redux/Actions/MacroActions';

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentBody from '@/Layouts/MainLayout/components/ContentBody';

import ContentBodyHeader from '@/Components/ResourcesCncPageComponents/ContentBodyHeader/ContentBodyHeader';
import EstimatedDeviation from './Components/EstimatedDeviation';
import ModalExporter from '@/Components/ModalExporter/ModalExporter';

import ProjectProgress from './Components/ProjectProgress';
import ProjectSCurve from './Components/ProjectSCurve';
import Skeleton from './Components/Skeleton';
import TasksStatus from './Components/TasksStatus';
import TaskFilters from '@/Components/TaskFilters/TaskFilters';

import './NewDashboardPage.scss'

const NewDashboardPage = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { projectLoaded, project, nonWorkingDays } = useProject()

  const { programmationLoaded } = useProgrammation()

  const { teams } = useTeams()

  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const macro = useSelector((state: any) => state.MacroReducer)

  const [ showExportModal, setShowExportModal ] = useState(false)
  const [ exportData, setExportData ] = useState<any | null>(null)
  const [ projectProgress, setProjectProgress ] = useState<any | null>(null)
  const [ estimatedEndDate, setEstimatedEndDate ] = useState('')
  const [ performance, setPerformance ] = useState(0)
  const [ deviation, setDeviation ] = useState(0)

  const [ showFilters, setShowFilters ] = useState(false)

  const [ filters, setFilters ] = useState({
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const projectStatus = useMemo(() => {

    if (!project) return null

    const isStarted = moment(project.startsAt).isBefore(moment())

    if (!isStarted) return 'pending-start'

    const isClosed = moment(project.endsAt).isBefore(moment())

    const isOnTime = isStarted && !isClosed && deviation === 0
    const isAdvanced = isStarted && !isClosed && deviation > 0
    const isOverdue = isStarted && !isClosed && deviation < 0

    if (isOnTime) return 'on-time'
    if (isAdvanced) return 'advanced'
    if (isOverdue) return 'overdue'

    if (isClosed) {
      if (!programmation.tasks.length) return 'closed-with-no-tasks'

      const uncompletedTasks = programmation.tasks.filter((task: any) => task.progress < 100)

      if (uncompletedTasks.length) return 'with-uncompleted-closure'

      return 'closed'
    }
    return null
  }, [project, deviation, programmation.tasks])

  const loadMacro = async (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      return {
        ...task,
        responsibles: task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      filters
    }

    let macroResult = getMacroResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      macroResult = filterByUsers(macroResult, users_ids)
    } else if (newFilters.user)
      macroResult = filterByUsers(macroResult, newFilters.user.split(','))

    if (newFilters.group) {

      macroResult = filterByGroups(macroResult, newFilters.group.split(','))
    }else if (newFilters.code)
      macroResult = filterByColorCodes(macroResult, newFilters.code.split(','))

    if (newFilters.type)
      macroResult = filterByTypes(macroResult, newFilters.type.split(','))

    if (newFilters.state)
      macroResult = filterByStatus(macroResult, newFilters.state.split(','))

    if (newFilters.start || newFilters.end)
      macroResult = filterByDate(macroResult, newFilters.start, newFilters.end)

    const collapsedGroups = getCollapsedGroups(project._id, 'macro')
    const collapsedTasks = getCollapsedTasks(project._id, 'macro')

    dispatch(setMacroAction(macroResult, project.startsAt, project.endsAt, collapsedGroups, collapsedTasks))
  }

  const hasActiveFilters = useMemo(() => {
    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }, [filters])

  useEffect(() => {

    const savedFilters = localStorage.getItem(`${project._id}-dashboard-filters`)

    if (savedFilters) {

      setFilters(JSON.parse(savedFilters))
    }
  }, [projectLoaded])

  useEffect(() => {

    if (programmationLoaded) {

      if (macro.loadStatus === 'none') {

        loadMacro()
      }
    }

  }, [ programmationLoaded, macro.loadStatus ])

  useEffect(() => {

    if (!projectLoaded || !programmationLoaded) return

    const { tasks, list, users, colorCodes } = programmation

    const excelFirstSheetData = getExcelFirstSheetData(tasks, list, users, colorCodes, nonWorkingDays, teams, filters, t)

    const {
      weeks: excelSecondSheetData,
      progress,
      estimatedEndDate,
      performance,
      deviation
    } = getExcelSecondSheetData(project, excelFirstSheetData, nonWorkingDays, t)

    console.table(excelSecondSheetData)

    setProjectProgress(progress)
    setEstimatedEndDate(estimatedEndDate)
    setPerformance(performance)
    setDeviation(deviation)

    const data = {
      firstSheet: excelFirstSheetData,
      secondSheet: excelSecondSheetData
    }

    setExportData(data)
  }, [projectLoaded, programmationLoaded, filters])

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project._id}-dashboard-filters`, JSON.stringify(filters))

    setFilters(filters)
  }

  const handleExport = async () => {

    setShowExportModal(true)
  }

  return (
    <ProjectLayout menuName="home" subMenuName="dashboard">
      <ContentBody noScroll>
        <ContentBodyHeader
          title={t('PROJECT_DASHBOARD:PageTitle')}
          buttons={[
            {
              icon: <i className="fas fa-filter"></i>,
              label: t('PROJECT_DASHBOARD:FiltersButton'),
              onClick: () => setShowFilters(true),
              variant: 'secondary',
              visible: true,
              active: showFilters || hasActiveFilters
            },
            {
              icon: <i className="fas fa-download"></i>,
              label: t('PROJECT_DASHBOARD:ExportButton'),
              onClick: handleExport,
              variant: 'primary',
              visible: true,
              active: programmationLoaded
            },
          ]}
        />
        <Row className="page-dashboard">
          <Col>
            <Row>
              <Col xs={12} lg={6} xl={4}  className='mb-4'>
                {!projectProgress && (
                  <Skeleton width={'100%'} height={320} borderRadius={10} count={1} />
                )}
                {projectProgress && (
                  <EstimatedDeviation
                    progress={(projectProgress.actual && projectProgress.projectMax) ? Math.round(projectProgress.actual / projectProgress.projectMax * 100) : 0}
                    deviation={deviation}
                    estimatedEndDate={estimatedEndDate}
                    performance={performance.toFixed(0)}
                    projectStatus={projectStatus}
                  />
                )}
              </Col>
              <Col xs={12} lg={6} xl={4} className='mb-4'>
                {!projectProgress && (
                  <Skeleton width={'100%'} height={320} borderRadius={10} count={1} />
                )}
                {projectProgress && (
                  <ProjectProgress
                    projectProgress={projectProgress}
                  />
                )}
              </Col>
              <Col xs={12} lg={6} xl={4} className='mb-4'>
                <TasksStatus tasks={exportData?.firstSheet} ready={!!exportData} />
              </Col>
            </Row>
            <Row>
              <Col>
                {!exportData && (
                  <Skeleton width={'100%'} height={420} borderRadius={10} count={1} />
                )}
                {exportData && (
                  <ProjectSCurve data={exportData?.secondSheet} />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </ContentBody>

      <ModalExporter
        show={showExportModal}
        title={t('MODAL_EXPORTER:DashboardExcelTitle')}
        file="dashboard-excel"
        exportData={exportData}
        onHide={() => setShowExportModal(false)}
      />

      {/* <FiltersModal
        show={showFilters}
        filters={{}}
        onChange={() => setShowFilters(false)}
        onHide={() => setShowFilters(false)}
      /> */}


      <TaskFilters
        show={showFilters}
        type="dashboard"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowFilters(false)}
      />

    </ProjectLayout>
  )
}

export default NewDashboardPage
